import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { isValidCedulaUY, normalizeCedulaUY, formatCedulaUY } from '../utils/cedula-uy';

type GeoState =
  | { status: 'loading' }
  | { status: 'ok'; lat: number; lng: number; accuracy: number }
  | { status: 'error'; message: string };

type CedulaState =
  | { status: 'empty' }
  | { status: 'typing'; raw: string }
  | { status: 'valid'; raw: string; normalized: string; formatted: string }
  | { status: 'invalid'; raw: string; message: string };

function validateCedula(raw: string): CedulaState {
  const stripped = raw.replace(/[\s.\-]/g, '');
  if (stripped.length === 0) return { status: 'empty' };
  if (stripped.length < 7) return { status: 'typing', raw };
  if (!/^\d+$/.test(stripped)) {
    return { status: 'invalid', raw, message: 'Solo dígitos, puntos o guiones.' };
  }
  if (stripped.length > 8) {
    return { status: 'invalid', raw, message: 'La cédula tiene máximo 8 dígitos.' };
  }
  if (!isValidCedulaUY(raw)) {
    return { status: 'invalid', raw, message: 'Dígito verificador incorrecto. Verificá el número.' };
  }
  const normalized = normalizeCedulaUY(raw)!;
  return { status: 'valid', raw, normalized, formatted: formatCedulaUY(normalized) };
}

export default function SignPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const padRef = useRef<SignaturePadHandle>(null);
  const [geo, setGeo] = useState<GeoState>({ status: 'loading' });
  const [cedula, setCedula] = useState<CedulaState>({ status: 'empty' });
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeo({ status: 'error', message: 'El dispositivo no soporta geolocalización' });
      return;
    }
    const watcher = navigator.geolocation.watchPosition(
      (pos) => setGeo({
        status: 'ok',
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (e) => setGeo({ status: 'error', message: e.message }),
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 },
    );
    return () => navigator.geolocation.clearWatch(watcher);
  }, []);

  function handleCedulaChange(e: React.ChangeEvent<HTMLInputElement>): void {
    setCedula(validateCedula(e.target.value));
  }

  async function submit(): Promise<void> {
    setErr(null);

    if (cedula.status === 'empty' || cedula.status === 'typing') {
      setErr('Ingresá la cédula del receptor antes de firmar.');
      return;
    }
    if (cedula.status === 'invalid') {
      setErr(`Cédula inválida: ${cedula.message}`);
      return;
    }
    if (padRef.current?.isEmpty()) {
      setErr('Capturá la firma antes de confirmar.');
      return;
    }
    if (geo.status !== 'ok') {
      setErr('Esperando señal GPS válida. Revisá permisos de ubicación.');
      return;
    }
    if (!id) return;

    setSubmitting(true);
    try {
      const dataUrl = padRef.current!.toDataUrl();
      const res = await api.sign(id, {
        signature_png_base64: dataUrl,
        receptor_cedula: cedula.normalized,
        location: { lat: geo.lat, lng: geo.lng, accuracy_m: geo.accuracy },
      });
      if (res.signed_url) window.open(res.signed_url, '_blank');
      nav('/');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const cedulaInputClass = (() => {
    if (cedula.status === 'valid') return 'input valid';
    if (cedula.status === 'invalid') return 'input error';
    return 'input';
  })();

  return (
    <>
      {submitting && (
        <div className="sign-overlay">
          <div className="spinner" />
          <div className="sign-overlay-text">Guardando firma…</div>
          <div className="sign-overlay-sub">Subiendo el acuse firmado al servidor</div>
        </div>
      )}
      <div className="header">Firmar entrega<div className="sub">ID {id?.slice(0, 8)}…</div></div>
      <div className="container">
        {err && <div className="alert error">{err}</div>}

        {/* Cédula del receptor */}
        <div className="card">
          <div className="label">Cédula de identidad del receptor</div>
          <input
            className={cedulaInputClass}
            type="text"
            inputMode="numeric"
            placeholder="Ej: 1.234.567-8"
            maxLength={12}
            value={cedula.status === 'empty' ? '' : cedula.raw}
            onChange={handleCedulaChange}
            autoComplete="off"
            autoFocus
          />
          {cedula.status === 'valid' && (
            <div className="field-hint success">✓ {cedula.formatted}</div>
          )}
          {cedula.status === 'invalid' && (
            <div className="field-hint error">{cedula.message}</div>
          )}
        </div>

        {/* Firma */}
        <div className="card">
          <div className="label">Firma del receptor</div>
          <SignaturePad ref={padRef} />
          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn secondary" onClick={() => padRef.current?.clear()}>Borrar</button>
            <button className="btn" disabled={submitting} onClick={submit}>
              {submitting ? 'Enviando…' : 'Confirmar firma'}
            </button>
          </div>
        </div>

        {/* GPS */}
        <div className="card">
          <div className="label">Ubicación GPS</div>
          {geo.status === 'loading' && <div className="gps">Obteniendo ubicación…</div>}
          {geo.status === 'ok' && (
            <div className="gps">
              {geo.lat.toFixed(6)}, {geo.lng.toFixed(6)} · precisión ±{geo.accuracy.toFixed(0)} m
            </div>
          )}
          {geo.status === 'error' && <div className="alert error">{geo.message}</div>}
        </div>
      </div>
    </>
  );
}
